import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, Spin, Tag, message } from "antd";
import {
  PrinterOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  ShoppingOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, orderProductSubtotal } from "../../../utils/formatters";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { printHtml } from "../../../shared/lib/printHtml";
import { PageHeader, T, cardStyle } from "../../../shared/ui/PageShell";
import usePermissions from "../../../hooks/usePermissions";

const MoneyRow = ({ label, value, color = T.text, strong = false, muted = false, tip }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 16,
      padding: strong ? "10px 0 0" : "7px 0",
      borderTop: strong ? `1px solid ${T.border}` : undefined,
      marginTop: strong ? 4 : 0,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <span style={{ fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 600, color: muted ? T.muted : T.sub }}>
        {label}
      </span>
      {tip ? <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{tip}</div> : null}
    </div>
    <span
      style={{
        fontSize: strong ? 18 : 14,
        fontWeight: strong ? 800 : 700,
        color,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </div>
);

const OrderView = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEditOrder = hasPermission("pos.edit") || hasPermission("orders.edit");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/orders/${id}`)
      .then((res) => setOrder(res.data))
      .catch(() => message.error(t("loadError", "Failed to load")))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 320 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!order) return null;

  const cfg = readCachedSystemConfig() || {};
  const mainCode = cfg.currency_code || "IQD";
  const money = (v) => formatCurrency(v, mainCode);
  const docNo = order.document_no || formatOrderNo(order.id, order.created_at);
  const nameOf = (p) => (p ? parseLocalizedName(p.name, i18n.language, `#${p.id}`) : t("product", "Product"));

  const lines = Array.isArray(order.order_products) ? order.order_products : [];
  const total = parseFloat(order.total_price || 0);
  const commission = parseFloat(order.commission_amount || 0);
  const isTaxed = !(order.is_taxed === false || order.is_taxed === 0);
  // Real product subtotal from lines — never total − commission (total may be denomination-neared).
  const subtotal = orderProductSubtotal(order) || orderProductSubtotal(lines);
  const paid = parseFloat(order.amount_paid || 0);
  const refunded = parseFloat(order.refunded_amount || 0);
  const totalReturned = parseFloat(order.total_returned || 0);
  const netPaid = parseFloat(order.net_paid != null ? order.net_paid : Math.max(0, paid - refunded));
  const remaining = parseFloat(order.outstanding_balance != null ? order.outstanding_balance : total - paid);

  const isCancelled = order.status === "cancelled";
  const isReturned = order.status === "returned";
  const hasPartialReturn = !isReturned && totalReturned > 0.0001;

  const [statusLabel, statusColor] = isCancelled
    ? [t("statusCancelled", "Cancelled"), "red"]
    : isReturned
      ? [t("statusReturned", "Returned"), "orange"]
      : netPaid <= 0.0001
        ? [t("statusUnpaid", "Unpaid"), "red"]
        : (netPaid < total - 0.0001 ? [t("statusPartial", "Partial"), "orange"] : [t("statusPaid", "Paid"), "green"]);

  const fmtQty = (r) => {
    const q = parseFloat(r.quantity);
    return Number.isFinite(q) && q > 0 ? q : null;
  };
  const fmtWeight = (r) => {
    const w = parseFloat(r.weight);
    return Number.isFinite(w) && w > 0 ? w : null;
  };

  const columns = [
    {
      title: "#",
      key: "idx",
      width: 48,
      render: (_, __, i) => <span style={{ color: T.muted, fontWeight: 600 }}>{i + 1}</span>,
    },
    {
      title: <b>{t("product", "Product")}</b>,
      key: "product",
      render: (_, r) => <span style={{ fontWeight: 700, color: T.text }}>{nameOf(r.product)}</span>,
    },
    {
      title: <b>{t("qty", "Qty")}</b>,
      key: "quantity",
      align: "center",
      width: 100,
      render: (_, r) => {
        const q = fmtQty(r);
        return q == null
          ? <span style={{ color: T.muted }}>—</span>
          : <Tag color="blue" style={{ margin: 0, fontWeight: 700 }}>{q}</Tag>;
      },
    },
    {
      title: <b>{t("weight", "Weight")}</b>,
      key: "weight",
      align: "center",
      width: 120,
      render: (_, r) => {
        const w = fmtWeight(r);
        return w == null
          ? <span style={{ color: T.muted }}>—</span>
          : <Tag color="green" style={{ margin: 0, fontWeight: 700 }}>{w} {t("kg", "kg")}</Tag>;
      },
    },
    {
      title: <b>{t("unitPrice", "Unit price")}</b>,
      key: "single_price",
      align: "right",
      width: 130,
      render: (_, r) => <span style={{ color: T.sub, fontVariantNumeric: "tabular-nums" }}>{money(r.single_price)}</span>,
    },
    {
      title: <b>{t("subtotal", "Subtotal")}</b>,
      key: "total_price",
      align: "right",
      width: 140,
      render: (_, r) => <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(r.total_price)}</b>,
    },
  ];

  const printReceipt = () => {
    const lang = String(i18n.language || "en").split("-")[0] || "en";
    const rtl = lang === "ku" || lang === "ar";
    const company = (cfg.invoice_company_name || "").trim() || cfg.site_name || "";
    const esc = (x) => String(x ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = lines.map((r) => {
      const q = fmtQty(r);
      const w = fmtWeight(r);
      return `<tr>
      <td>${esc(nameOf(r.product))}</td>
      <td style="text-align:center">${q == null ? "—" : esc(String(q))}</td>
      <td style="text-align:center">${w == null ? "—" : esc(`${w} ${t("kg", "kg")}`)}</td>
      <td style="text-align:right">${money(r.single_price)}</td>
      <td style="text-align:right"><b>${money(r.total_price)}</b></td>
    </tr>`;
    }).join("");
    const html = `<!doctype html><html dir="${rtl ? "rtl" : "ltr"}" lang="${esc(lang)}"><head><meta charset="utf-8"><style>
      @page{size:A4 portrait;margin:14mm}
      body{font-family:Tahoma,'Segoe UI',Arial,sans-serif;color:#111;font-size:13px;direction:${rtl ? "rtl" : "ltr"}}
      h1{font-size:22px;margin:0}
      table{width:100%;border-collapse:collapse;margin-top:10px}
      th,td{padding:7px 9px;border-bottom:1px solid #eee;text-align:${rtl ? "right" : "left"}}
      th{font-size:11px;text-transform:uppercase;color:#666}
      .box{border:1px solid #ddd;border-radius:8px;padding:12px 16px;margin-top:14px}
      .row{display:flex;justify-content:space-between;padding:4px 0}
    </style></head><body>
      <div style="text-align:center;margin-bottom:10px"><h1>${esc(company)}</h1><div style="color:#666">${t("orderReceipt", "Order Receipt")} ${esc(docNo)}</div></div>
      <div style="display:flex;justify-content:space-between">
        <div><b>${esc(order.customer?.name || t("walkInCustomer", "Walk-in"))}</b>${order.customer?.phone_number ? "<br>" + esc(order.customer.phone_number) : ""}</div>
        <div style="text-align:${rtl ? "left" : "right"};color:#666">${order.created_at ? dayjs(order.created_at).format("YYYY-MM-DD HH:mm") : ""}${order.source === "pos" ? `<br>${esc(t("sourcePos", "POS"))}` : ""}</div>
      </div>
      <table><thead><tr>
        <th>${t("product", "Product")}</th>
        <th style="text-align:center">${t("qty", "Qty")}</th>
        <th style="text-align:center">${t("weight", "Weight")}</th>
        <th style="text-align:right">${t("unitPrice", "Unit price")}</th>
        <th style="text-align:right">${t("subtotal", "Subtotal")}</th>
      </tr></thead><tbody>${rows || `<tr><td colspan="5" style="text-align:center;color:#999">—</td></tr>`}</tbody></table>
      <div class="box">
        ${isTaxed && commission > 0 ? `
        <div class="row"><span>${esc(t("posTerminalSubtotal", "Subtotal"))}</span><b>${money(subtotal)}</b></div>
        <div class="row"><span>${esc(t("posTerminalCommission", "Commission"))}</span><b style="color:#d97706">+ ${money(commission)}</b></div>
        ` : ""}
        <div class="row"><span>${esc(t("totalPrice", "Total"))}</span><b>${money(total)}</b></div>
        <div class="row"><span>${esc(t("amountPaid", "Gross paid"))}</span><b>${money(paid)}</b></div>
        <div class="row"><span>${esc(t("refunded", "Refunded"))}</span><b style="color:#e11d48">− ${money(refunded)}</b></div>
        <div class="row"><span>${esc(t("netPaid", "Net paid"))}</span><b style="color:#16a34a">${money(netPaid)}</b></div>
        ${order.cancellation_reason ? `<div class="row"><span>${esc(t("reason", "Cancellation reason"))}</span><b>${esc(order.cancellation_reason)}</b></div>` : ""}
        <div class="row" style="border-top:1px solid #ccc;margin-top:6px;padding-top:8px;font-size:16px"><b>${esc(t("remaining", "Remaining"))}</b><b style="color:${remaining > 0.0001 ? "#e11d48" : "#16a34a"}">${money(remaining)}</b></div>
      </div>
    </body></html>`;
    printHtml(html);
  };

  const remainingOwed = remaining > 0.0001;

  return (
    <div className="velan-view-page order-view-page" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("orders", "Orders"), docNo]}
        title={`${t("orderReceipt", "Order")} ${docNo}`}
        subtitle={
          <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: T.sub, fontWeight: 600 }}>
              {order.created_at ? dayjs(order.created_at).format("DD/MM/YYYY HH:mm") : "—"}
            </span>
            <Tag color={statusColor} style={{ fontWeight: 700, borderRadius: 6 }}>{statusLabel}</Tag>
            {order.source === "pos" && <Tag color="blue" style={{ borderRadius: 6 }}>{t("sourcePos", "POS")}</Tag>}
            {hasPartialReturn && (
              <Tag color="orange" style={{ fontWeight: 700, borderRadius: 6 }}>
                {t("statusPartialReturn", "Partial return")}
              </Tag>
            )}
          </span>
        }
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button icon={<ArrowLeftOutlined />} style={{ borderRadius: 9 }} onClick={() => navigate(-1)}>
              {t("back", "Back")}
            </Button>
            {canEditOrder && !isCancelled && !isReturned && (
              <Button
                icon={<EditOutlined />}
                style={{ borderRadius: 9 }}
                onClick={() => navigate(`/orders/${order.id}/edit`)}
              >
                {t("editOrder", "Edit Order")}
              </Button>
            )}
            <Button type="primary" icon={<PrinterOutlined />} style={{ borderRadius: 9 }} onClick={printReceipt}>
              {t("print", "Print")}
            </Button>
          </div>
        }
      />

      {/* Top focus: Total + Remaining */}
      <div className="velan-stats-row" style={{ marginBottom: 14 }}>
        <div className="velan-tint-card velan-tint-card--blue" style={{ ...cardStyle, padding: "18px 20px" }}>
          <div className="velan-tint-card__label">
            {t("totalPrice", "Total")}
          </div>
          <div className="velan-tint-card__value" style={{ fontSize: 28, fontWeight: 800, color: T.text, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
            {money(total)}
          </div>
        </div>
        <div
          className={`velan-tint-card ${remainingOwed ? "velan-tint-card--rose" : "velan-tint-card--green"}`}
          style={{ ...cardStyle, padding: "18px 20px" }}
        >
          <div className="velan-tint-card__label">
            {t("remaining", "Remaining")}
          </div>
          <div
            className="velan-tint-card__value"
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: remainingOwed ? T.red : T.green,
              marginTop: 6,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money(remaining)}
          </div>
        </div>
        <div className="velan-tint-card velan-tint-card--green" style={{ ...cardStyle, padding: "18px 20px" }}>
          <div className="velan-tint-card__label">
            {t("netPaid", "Net paid")}
          </div>
          <div className="velan-tint-card__value" style={{ fontSize: 28, fontWeight: 800, color: T.green, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
            {money(netPaid)}
          </div>
        </div>
      </div>

      {/* Customer + payment breakdown */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.15fr)",
          gap: 14,
          marginBottom: 14,
        }}
        className="order-view-split"
      >
        <div style={{ ...cardStyle, padding: 0 }}>
          <div className="velan-card-head" style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: T.text }}>{t("customer", "Customer")}</span>
          </div>
          <div style={{ padding: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              className="velan-avatar-soft velan-avatar-soft--sky"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <UserOutlined style={{ fontSize: 18 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.text, lineHeight: 1.3 }}>
                {order.customer?.name || t("walkInCustomer", "Walk-in customer")}
              </div>
              {order.customer?.phone_number ? (
                <div style={{ marginTop: 6, fontSize: 13, color: T.sub, display: "flex", alignItems: "center", gap: 6 }}>
                  <PhoneOutlined />
                  <span dir="ltr">{order.customer.phone_number}</span>
                </div>
              ) : (
                <div style={{ marginTop: 6, fontSize: 12, color: T.muted }}>{t("noPhone", "No phone")}</div>
              )}
              {order.cancellation_reason ? (
                <div className="velan-alert-soft velan-alert-soft--rose" style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, fontSize: 12, color: T.red }}>
                  <b>{t("reason", "Reason")}:</b> {order.cancellation_reason}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ ...cardStyle, padding: 0 }}>
          <div className="velan-card-head" style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: T.text }}>{t("paymentSummary", "Payment summary")}</span>
          </div>
          <div style={{ padding: "10px 16px 14px" }}>
            {isTaxed && commission > 0 && (
              <>
                <MoneyRow label={t("posTerminalSubtotal", "Subtotal")} value={money(subtotal)} muted />
                <MoneyRow
                  label={t("posTerminalCommission", "Commission")}
                  value={`+ ${money(commission)}`}
                  color="#d97706"
                />
              </>
            )}
            <MoneyRow label={t("totalPrice", "Total")} value={money(total)} />
            <MoneyRow label={t("amountPaid", "Gross paid")} value={money(paid)} />
            {(refunded > 0.0001 || totalReturned > 0.0001) && (
              <MoneyRow
                label={t("refunded", "Refunded")}
                value={`− ${money(Math.max(refunded, totalReturned))}`}
                color={T.red}
                tip={hasPartialReturn || isReturned ? t("includesReturns", "Includes returns on this order") : undefined}
              />
            )}
            <MoneyRow label={t("netPaid", "Net paid")} value={money(netPaid)} color={T.green} />
            <MoneyRow
              label={t("remaining", "Remaining")}
              value={money(remaining)}
              color={remainingOwed ? T.red : T.green}
              strong
            />
          </div>
        </div>
      </div>

      {/* Line items */}
      <div style={cardStyle}>
        <div
          className="velan-card-head"
          style={{
            padding: "12px 16px",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <ShoppingOutlined style={{ color: T.primary }} />
          <span style={{ fontWeight: 800, fontSize: 14, color: T.text }}>{t("orderItems", "Items")}</span>
          <Tag style={{ marginInlineStart: 4, borderRadius: 6 }}>{lines.length}</Tag>
        </div>
        <Table
          size="middle"
          dataSource={lines.map((l, i) => ({ ...l, key: l.id ?? i }))}
          columns={columns}
          pagination={false}
          scroll={{ x: 720 }}
          locale={{ emptyText: t("noDataFound", "No items") }}
        />
      </div>

      <style>{`
        @media (max-width: 800px) {
          .order-view-split { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default OrderView;
